import type { TournamentMatch } from "@mixitone/models";
import { getMatchEntryIds } from "tournament-state/tournamentMatchEligibility";
import { getMatchSlotId } from "tournament-state/TournamentProjection";

export type EntryMatchResult = "won" | "lost" | "voided" | null;

export function canRecordEntryMatchResult(params: {
  result: EntryMatchResult;
  match: TournamentMatch;
}): boolean {
  if (params.result != null) return false;
  if (params.match.isVoided || params.match.status === "voided") return false;
  if ((params.match.entries?.winners?.length ?? 0) > 0) return false;
  return getMatchEntryIds(params.match).length >= 2;
}

export function winnerIdForEntryMatchResult(
  entryId: string,
  match: TournamentMatch,
  result: "won" | "lost",
): string | undefined {
  if (result === "won") return entryId;
  return getMatchEntryIds(match).find((id) => id !== entryId);
}

function slotDivision(match: TournamentMatch): number | undefined {
  const divRaw = match.format_data?.division;
  if (typeof divRaw === "number" && Number.isFinite(divRaw)) return divRaw;
  if (typeof divRaw === "string") {
    const n = Number.parseInt(divRaw, 10);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

export function entryMatchWinnerSelectedPayload(params: {
  entryId: string;
  match: TournamentMatch;
  result: "won" | "lost";
}):
  | {
      slot: {
        slot_id: string;
        round: number;
        match_index: number;
        division?: number;
      };
      winner_id: string;
      entries: string[];
    }
  | undefined {
  if (!canRecordEntryMatchResult({ result: null, match: params.match })) return undefined;
  const winnerId = winnerIdForEntryMatchResult(params.entryId, params.match, params.result);
  if (!winnerId) return undefined;
  const division = slotDivision(params.match);
  return {
    slot: {
      slot_id: getMatchSlotId(params.match),
      round: params.match.round || 0,
      match_index: params.match.match_index || 0,
      ...(division !== undefined ? { division } : {}),
    },
    winner_id: winnerId,
    entries: getMatchEntryIds(params.match),
  };
}
